import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor() {}

  /**
   * Export data to Excel
   * @param data - The array of objects to export
   * @param fileName - The name of the Excel file (optional, default: 'ExportedData.xlsx')
   * @param sheetName - The name of the worksheet (optional, default: 'Sheet1')
   */
  exportToExcel(data: any[], fileName: string = 'ExportedData.xlsx', sheetName: string = 'Sheet1'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate the Excel file buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer and save as file
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /**
   * Export data to CSV
   * @param data - The array of objects to export
   * @param fileName - The name of the CSV file
   */
  exportToCSV(data: any[], fileName: string = 'ExportedData.csv'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName.endsWith('.csv') ? fileName : `${fileName}.csv`);
  }

  /**
   * Export data directly to PDF file download
   * @param data - The array of objects to export
   * @param fileName - The name of the PDF file
   * @param title - The title of the document
   */
  exportToPDF(data: any[], fileName: string = 'ExportedData.pdf', title: string = 'Exported Report'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const headers = Object.keys(data[0]);
      const rows = data.map(item =>
        headers.map(header => item[header] !== undefined && item[header] !== null ? String(item[header]) : '')
      );

      // Title & Metadata
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      doc.text(title, 40, 35);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 40, 50);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 60,
        styles: {
          fontSize: 7,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        theme: 'grid'
      });

      const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      doc.save(pdfFileName);
    } catch (e) {
      console.error('jsPDF export error, falling back to print window:', e);
      const headers = Object.keys(data[0]);
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF.');
        return;
      }

      const rowsHtml = data.map(row => `
        <tr>
          ${headers.map(h => `<td style="border: 1px solid #e2e8f0; padding: 7px 6px; font-size: 10px;">${row[h] !== undefined && row[h] !== null ? row[h] : ''}</td>`).join('')}
        </tr>
      `).join('');

      const headersHtml = headers.map(h => `<th style="border: 1px solid #cbd5e1; background-color: #2563eb; color: #ffffff; padding: 8px 6px; font-size: 10px; text-align: left; white-space: nowrap;">${h}</th>`).join('');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 15px; color: #1e293b; }
            h2 { color: #1e293b; font-size: 18px; margin-bottom: 4px; }
            p.subtitle { color: #64748b; font-size: 11px; margin-top: 0; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            tr:nth-child(even) { background-color: #f8fafc; }
            @media print {
              body { margin: 0; }
              @page { size: landscape; margin: 8mm; }
            }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>${headersHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  }
}
